import { ConfigProvider } from '~/app-modules/core/ioc/config.provider';
import { ServerLoggerClient } from '~/app-modules/logger/clients/server-logger.client';
import {
  LoggingLevels,
  LoggingLevelsToConsoleMethod,
} from '~/app-modules/logger/types/logging-levels';
import { FetchError } from 'ofetch';
import { ServerLogDto } from '~/app-modules/logger/clients/dto/server-log.dto';
import { useLastRoute } from '~/app-modules/core/composables/use-last-route';
import axios from 'axios';

@singleton()
export class Logger {
  constructor(
    protected configProvider: ConfigProvider,
    protected serverLoggerClient: ServerLoggerClient,
  ) {
    const { logger } = configProvider.getPublicRuntimeConfig();
    this._logLevel = logger.logLevel;
  }

  private readonly _logLevel: LoggingLevels;

  private async _log(logAttrs: { message: string; level?: LoggingLevels; error?: Error }) {
    const { message, level = LoggingLevels.info, error } = logAttrs;

    const loggingFrom = import.meta.browser ? 'browser' : 'server';
    const currentRoute = useRoute().fullPath;
    const lastRoute = useLastRoute().value.fullPath;

    const logObject: ServerLogDto = {
      '@timestamp': new Date().toISOString(),
      level: LoggingLevels[level],
      message,
      context: {
        from: loggingFrom,
        currentRoute,
        lastRoute,
      },
    };

    if (error) {
      logObject.context.errorName = error.name;
      logObject.context.errorStack = error.stack;
      logObject.context.errorMessage = error.message;
    }

    if (axios.isAxiosError(error) && error.response) {
      logObject.context.errorCode = error.response.status;
      logObject.context.errorRequestId = error.response?.data?.requestId;
      logObject.context.errorSubCode = error.response?.data?.errorCode;
    }

    LoggingLevelsToConsoleMethod[level](logObject);

    if (import.meta.browser) {
      try {
        await this.serverLoggerClient.log(logObject);
      } catch (e) {
        if (e instanceof FetchError) {
          console.error(e);
        }
      }
    }
  }

  public debug(message: string) {
    if (this._logLevel >= LoggingLevels.debug) {
      this._log({ message, level: LoggingLevels.debug });
    }
  }

  public info(message: string) {
    if (this._logLevel >= LoggingLevels.info) {
      this._log({ message, level: LoggingLevels.info });
    }
  }

  public warn(message: string) {
    if (this._logLevel >= LoggingLevels.warning) {
      this._log({ message, level: LoggingLevels.warning });
    }
  }

  public error(message: string) {
    if (this._logLevel >= LoggingLevels.error) {
      this._log({ message, level: LoggingLevels.error });
    }
  }

  public logError(message: string, error: Error) {
    if (this._logLevel >= LoggingLevels.error) {
      this._log({
        message,
        error,
        level: LoggingLevels.error,
      });
    }
  }
}
